import axios from "axios";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function mediaUrl(url) {
  if (!url) return url;
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

const UNIT_SUFFIX = { hr: "/hr", hour: "/hr", week: "/wk", day: "/day", night: "/night", guest: "/guest", year: "/yr", event: "/event" };
const UNIT_LONG = { hr: "per flight hour", hour: "per hour", week: "per week", day: "per day", night: "per night", guest: "per guest", year: "per year", event: "per event" };

export function unitSuffix(unit) { return unit ? (UNIT_SUFFIX[unit] || `/${unit}`) : ""; }
export function unitLong(unit) { return unit ? (UNIT_LONG[unit] || `per ${unit}`) : ""; }
export function fmtMoney(n) { return `$${Number(n).toLocaleString()}`; }

// Resolve a listing's starting price -> { amount, unit } or null.
// Every listing with a price_from shows an indicative "starting from" price,
// even quote-only ones (exact terms are confirmed via the quote request).
export function listingPrice(listing) {
  if (!listing) return null;
  const amount = listing.price_from != null ? listing.price_from : (listing.price_per_day != null ? listing.price_per_day : null);
  if (!amount) return null;
  const unit = listing.price_unit || (listing.price_from == null && listing.price_per_day != null ? "day" : null);
  return { amount: Number(amount), unit };
}

export function priceLabel(listing) {
  const p = listingPrice(listing);
  if (!p) return "Request a Quote";
  return `From ${fmtMoney(p.amount)}${unitSuffix(p.unit)}`;
}

export function vipPrice(price, discount) {
  if (!price) return null;
  return Math.round(Number(price) * (1 - (Number(discount) || 0) / 100));
}

export function waLink(number, text) {
  const n = (number || "").replace(/[^0-9]/g, "");
  if (!n) return null;
  return `https://wa.me/${n}?text=${encodeURIComponent(text || "")}`;
}

export function waMessageFor(listing) {
  if (!listing) return "Hello LuxuryCharterKings, I'd like to speak with a concierge about your collection.";
  return `Hello LuxuryCharterKings, I'm interested in the ${listing.name} in ${listing.location}. Please let me know the availability and rental price.`;
}

// Category-adaptive card metadata: [{label, value}]
export function cardMeta(l) {
  const out = [];
  const specs = Object.fromEntries((l.specs || []).filter((s) => Array.isArray(s)));
  if (l.category === "cars") {
    if (l.passenger_capacity) out.push({ icon: "users", value: `${l.passenger_capacity} seats` });
    if (l.chauffeur_option) out.push({ icon: "key", value: l.chauffeur_option });
  } else if (l.category === "jets") {
    if (l.passenger_capacity) out.push({ icon: "users", value: `${l.passenger_capacity} pax` });
    if (specs.Range) out.push({ icon: "compass", value: specs.Range });
  } else if (l.category === "yachts") {
    if (l.passenger_capacity) out.push({ icon: "users", value: `${l.passenger_capacity} guests` });
    if (specs.Length) out.push({ icon: "ruler", value: specs.Length });
  } else if (l.category === "villas") {
    if (l.bedrooms) out.push({ icon: "bed", value: `${l.bedrooms} beds` });
    if (l.guests || l.passenger_capacity) out.push({ icon: "users", value: `${l.guests || l.passenger_capacity} guests` });
  } else if (l.category === "tours") {
    if (specs.Duration) out.push({ icon: "clock", value: specs.Duration });
    if (specs.Guests || l.passenger_capacity) out.push({ icon: "users", value: specs.Guests || `${l.passenger_capacity}` });
  } else if (l.category === "vip") {
    if (specs.Availability) out.push({ icon: "clock", value: specs.Availability });
    if (specs.Coverage) out.push({ icon: "compass", value: specs.Coverage });
  }
  return out.slice(0, 2);
}

export default api;
